<template>
  <div id="retirement-calculator" class='bg-white'>
    <Breadcrumbs current-page-name="Retirement calculator" />
    <div class="flex flex-col gap-8 bg-white py-20 max-w-5xl mx-auto">
      <!-- Header Section -->
      <div class="text-center">
        <h3 class="font-bold text-blue-1">Retirement calculator</h3>
        <p class="paragraph-3 mt-4 flex items-center justify-center gap-1">Powered by <img alt='otivo-logo' src='@/assets/img/otivo-small.png' class="h-[1.5em] w-auto"/></p>
      </div>

      <!-- Initial User Details (before first calculation) -->
      <div v-if="!displaySummary" class="w-full">
        <UserDetails/>
      </div>

      <div v-if="displaySummary" class="w-full">
        <OtivoRetirementCharts />
      </div>

      <div v-if="displaySummary" class="w-full">
        <RetirementCalculatorTabs />
      </div>

      <div v-if="displaySummary" class="flex flex-col w-full md:max-w-[280px] items-center gap-4 mx-auto">
        <OtivoButton class='w-full' :loading="graphLoading" @click="updateCalculation">Update</OtivoButton>
        <OtivoButton class='w-full' colour="white" @click="startAgain">Start again</OtivoButton>
      </div>
      <OtivoButton v-else class='mx-auto px-4 w-full sm:w-fit' :loading='graphLoading' @click='updateCalculation'>Next</OtivoButton>
    </div>
      <div class='bg-blue-1 w-full py-16 px-4 mx-auto text-center flex flex-col'>
        <p class='intro-2 text-white max-w-[560px] mx-auto text-xl'>Don't leave your retirement to chance. Get a personalised plan with licensed professional advice from Otivo.</p>
        <FreeToStartButton colour='white' class='mt-10' />
      </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import RetirementCalculatorTabs from './RetirementCalculatorTabs.vue'
import UserDetails from './UserDetails.vue'
import OtivoButton from '@/components/OtivoButton.vue'
import OtivoRetirementCharts from './OtivoRetirementCharts.vue'
import { useRetirementCalculatorStore } from '@/store/pinia/RetirementCalculatorStore'
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue'
import FreeToStartButton from '@/app/components/home/FreeToStartButton.vue'
import { useHead } from '@unhead/vue'
import { addStructuredData, createBreadcrumbSchema, createOrganizationSchema, createStandardMeta } from '@/utils/seo.ts'

// Store setup
const retirementCalculatorStore = useRetirementCalculatorStore()

// Computed properties to control display flow
const displaySummary = computed(() => retirementCalculatorStore.getDisplaySummary)
const graphLoading = computed(() => retirementCalculatorStore.getGraphLoading)

const updateCalculation = async () => {
  try {
    const success = await retirementCalculatorStore.validateAndCalculate()
    if (!success) {
      return
    }
  } catch (error) {
    console.error('Error during retirement calculation:', error)
  }
}

const startAgain = async () => {
  // Reset all data using the same method as CFS
  await retirementCalculatorStore.refreshRetCalc()
}

// SEO for advice practices service page
const seoData = createStandardMeta({
  title: 'Otivo. Be better off. - Retirement calculator',
  description: '',
  keywords: 'financial advice, digital solutions, retirement, calculator, retirment calculator, australia, otivo'
})

const structuredData = addStructuredData([
  createOrganizationSchema(),
  createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Resources', url: '/resources' },
    { name: 'Otivo Retirement Calculator' }
  ])
])

useHead({
  ...seoData,
  ...structuredData
})
</script>
